import { faPencil, faTrash, faUpload } from "@fortawesome/pro-duotone-svg-icons";
import { Alert, Button, Checkbox, FileInput, Flex, Grid, Group, Text, TextInput, Tooltip } from "@mantine/core";
import { AxiosError } from "axios";
import { useEffect, useState } from "react";
import { Icon } from "../../../custom-components/icons";
import Modal from "../../../custom-components/model";
import PopConfirm from "../../../custom-components/pop-confirm";
import { TableData, TableView } from "../../../custom-components/table";
import { InsuranceCodeType } from "../../../models/models";
import theme from "../../../stores/theme";
import t from "../../../stores/language";
import { useStore } from "../../../stores/store";
import { useStyles } from "../configure-input";
import { failedReasonSummary } from "./insurance-data";
import { downloadCodeTypeTemplate, readUploadRows } from "./insurance-sheet";

const formatUpdatedOn = (value: string) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value.trim());
  if (match == null) {
    return value;
  }
  return `${Number(match[2])}/${Number(match[3])}/${match[1]}`;
};

const toRow = (item: InsuranceCodeType): TableData => ({
  id: item.id,
  codeType: item.codeType,
  percentage: `${item.percentage}%`,
  lastUpdatedAt: formatUpdatedOn(item.lastUpdatedAt),
});

const apiError = (error: unknown) => {
  if (error instanceof Error && error.message !== "" && !(error instanceof AxiosError)) {
    return error.message;
  }
  const data = (error as AxiosError).response?.data;
  return typeof data === "string" && data !== "" ? data : "Save Failed";
};

const InsuranceMaster = () => {
  const { classes } = useStyles();
  const { store } = useStore();
  const [addOpen, setAddOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadMessage, setUploadMessage] = useState<{ isError: boolean; message: string }>({ isError: false, message: "" });
  const [addMessage, setAddMessage] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewRows, setPreviewRows] = useState<TableData[]>([]);
  const [updateExisting, setUpdateExisting] = useState(false);
  const [codeType, setCodeType] = useState("");
  const [percentage, setPercentage] = useState("");
  const [editingId, setEditingId] = useState<number | null>(null);
  const [rows, setRows] = useState<TableData[]>([]);
  const [selectedRows, setSelectedRows] = useState<TableData[]>([]);
  const [actionError, setActionError] = useState("");

  const loadCodeTypes = async () => {
    const codeTypes = await store.GetInsuranceCodeTypes();
    const nextRows = codeTypes.map(toRow);
    setRows(nextRows);
    setSelectedRows((current) => current.filter((row) => nextRows.some((item) => item.id == row.id)));
    return codeTypes;
  };

  useEffect(() => {
    loadCodeTypes().catch(() => setRows([]));
  }, []);

  const closeEditor = () => {
    setAddOpen(false);
    setEditingId(null);
    setCodeType("");
    setPercentage("");
    setAddMessage("");
  };

  const openEditor = (row?: { [key: string]: string | null | number }) => {
    setActionError("");
    setAddMessage("");
    if (row == null) {
      setEditingId(null);
      setCodeType("");
      setPercentage("");
    } else {
      setEditingId(Number(row.id));
      setCodeType(String(row.codeType ?? ""));
      setPercentage(String(row.percentage ?? "").replace(/%$/, ""));
    }
    setAddOpen(true);
  };

  const saveEditor = () => {
    const percentageValue = Number(percentage);
    if (codeType.trim() === "" || percentage === "" || percentage === "." || Number.isNaN(percentageValue) || percentageValue < 0 || percentageValue > 100) {
      setAddMessage(t("Code Type Percentage must be a number from 0 to 100"));
      return;
    }
    const displayPercentage = percentage.endsWith(".") ? percentage.slice(0, -1) : percentage;
    const request =
      editingId == null
        ? store.AddInsuranceCodeType(codeType.trim(), displayPercentage)
        : store.UpdateInsuranceCodeType(editingId, codeType.trim(), displayPercentage);
    request
      .then(() => loadCodeTypes())
      .then(() => closeEditor())
      .catch((error) => setAddMessage(apiError(error)));
  };

  const deleteCodeType = (id: number) => {
    setActionError("");
    store
      .DeleteInsuranceCodeType(id)
      .then(() => loadCodeTypes())
      .catch((error) => setActionError(apiError(error)));
  };

  const deleteSelected = () => {
    setActionError("");
    store
      .BulkDeleteInsuranceCodeTypes(selectedRows.map((row) => Number(row.id)))
      .then(() => loadCodeTypes())
      .catch((error) => setActionError(apiError(error)));
  };

  const handleSelectAll = (checked: boolean) => {
    setSelectedRows(checked ? rows : []);
  };

  const allSelected = selectedRows.length > 0 && selectedRows.length === rows.length;

  const rowSelection = (row: { [key: string]: string | null | number }) => {
    return (
      <Flex align={"center"} justify={"center"}>
        <Checkbox
          label={""}
          checked={selectedRows.find((item) => item.id == row.id) != null}
          onChange={() => {
            const checked = selectedRows.find((item) => item.id == row.id);
            if (checked == null) {
              setSelectedRows([...selectedRows, row]);
            } else {
              setSelectedRows(selectedRows.filter((item) => item.id != row.id));
            }
          }}
          size={"xs"}
        />
      </Flex>
    );
  };

  const saveCodeTypes = async (items: TableData[]) => {
    const result = await store.BulkUploadInsuranceCodeTypes(
      items.map((item) => ({
        codeType: String(item.codeType ?? "").trim(),
        percentage: String(item.percentage ?? "").replace(/%$/, ""),
      }))
    );
    await loadCodeTypes();
    if (result.errors.length > 0) {
      throw new Error(result.errors.map((error) => `${error.codeType ?? ""}: ${error.reason}`).join(". "));
    }
  };

  const getActions = (row: { [key: string]: string | null | number }) => {
    return (
      <Flex>
        <Tooltip label={t("Edit")} withArrow withinPortal>
          <Button size="xs" variant="subtle" compact className={classes.iconButton} onClick={() => openEditor(row)}>
            <Icon icon={faPencil} size="xl" />
          </Button>
        </Tooltip>
        <PopConfirm confirmText={t("Are You Sure?")} width={180} onConfirm={() => deleteCodeType(Number(row.id))}>
          <Tooltip label={t("Delete")} withArrow withinPortal>
            <Button size="xs" variant="subtle" compact className={classes.iconButton}>
              <Icon icon={faTrash} size="xl" color="red" />
            </Button>
          </Tooltip>
        </PopConfirm>
      </Flex>
    );
  };

  const closePreview = () => {
    setPreviewOpen(false);
    setUpdateExisting(false);
  };

  const previewForUpload = previewRows.map((row) => (updateExisting && row.canOverride === 1 ? { ...row, reason: "" } : row));
  const errorRecordCount = previewForUpload.filter((row) => row.reason !== "").length;
  const uploadRows = previewForUpload.filter((row) => row.reason === "");
  const canUpdateExisting = previewRows.some((row) => row.canOverride === 1);

  return (
    <div className={classes.reportsContainer}>
      <div className={classes.title} style={{ alignItems: "center" }}>
        <span>{t("Master Insurance Data")}</span>
        <Group spacing="sm">
          {selectedRows.length > 0 ? (
            <PopConfirm confirmText={t("Are You Sure?")} width={180} onConfirm={deleteSelected}>
              <Button size="sm" color="red" variant="light">
                {t(allSelected ? "Delete All" : "Delete Selected")}
              </Button>
            </PopConfirm>
          ) : null}
          <Button size="sm" variant="default" onClick={() => setUploadOpen(true)}>
            {t("Bulk Upload")}
          </Button>
          <Button size="sm" onClick={() => openEditor()}>
            {t("Add Code Type")}
          </Button>
        </Group>
      </div>
      <TableView
        data={rows.map((row) => ({
          ...row,
          percentage: String(row.percentage).endsWith("%") ? row.percentage : `${row.percentage}%`,
        }))}
        columns={[
          { key: "codeType", label: "Code Type" },
          { key: "percentage", label: "Code Type Percentage (%)", width: "16%", dataAlign: "right" },
          { key: "lastUpdatedAt", label: "Last Update At", width: "12%", dataAlign: "right" },
        ]}
        actions={getActions}
        actionsWidth={90}
        selectRows={rowSelection}
        onSelectAll={handleSelectAll}
        allSelected={allSelected}
        fixedLayout
        height={"calc(100vh - 206px)"}
      />
      {actionError !== "" ? (
        <Text size={theme.fontSizes?.sm} color="red" mt={8}>
          {actionError}
        </Text>
      ) : null}
      <Modal
        opened={addOpen}
        header={editingId == null ? t("Add Code Type") : t("Edit Code Type")}
        footer={
          <Group className={classes.modalFooter}>
            <Button variant="default" onClick={closeEditor}>
              {t("Cancel")}
            </Button>
            <Button onClick={saveEditor}>{t("Save")}</Button>
          </Group>
        }
        withCloseButton={true}
        onClose={closeEditor}
        size="35%"
      >
        <Grid pb={15} pl={15} pr={15} columns={12} m={0}>
          <Grid.Col span={12}>
            <TextInput label={t("Code Type")} radius={0} value={codeType} onChange={(event) => setCodeType(event.target.value)} />
          </Grid.Col>
          <Grid.Col span={12}>
            <TextInput
              label={t("Code Type Percentage")}
              radius={0}
              value={percentage}
              inputMode="decimal"
              rightSection="%"
              rightSectionWidth={36}
              onChange={(event) => {
                const nextValue = event.target.value;
                if (!/^\d*\.?\d{0,2}$/.test(nextValue)) {
                  return;
                }
                if (nextValue === "" || nextValue === "." || Number(nextValue) <= 100) {
                  setPercentage(nextValue);
                }
              }}
            />
          </Grid.Col>
          {addMessage !== "" ? (
            <Grid.Col span={12}>
              <Text size={theme.fontSizes?.sm} color="red">
                {addMessage}
              </Text>
            </Grid.Col>
          ) : null}
        </Grid>
      </Modal>
      <Modal
        opened={uploadOpen}
        header={t("Bulk Upload")}
        footer={
          <Group className={classes.modalFooter}>
            <Button
              variant="default"
              onClick={() => {
                setUploadOpen(false);
                setUploadFile(null);
                setUploadMessage({ isError: false, message: "" });
              }}
            >
              {t("Cancel")}
            </Button>
            <Button
              onClick={() => {
                // TODO: Remove this dummy bulk upload. It only updates the table in the browser and will be replaced by the real save.
                if (uploadFile == null) {
                  setUploadMessage({ isError: true, message: t("Please Select File") });
                  return;
                }
                readUploadRows(uploadFile)
                  .then(async (sheetRows) => {
                    const existing = await store.GetInsuranceCodeTypes().catch((error) => {
                      setUploadMessage({ isError: true, message: apiError(error) });
                      return null;
                    });
                    if (existing == null) {
                      return;
                    }
                    const existingCodeTypes = new Map(
                      existing.map((item) => [item.codeType.trim().toLowerCase(), String(item.percentage).replace(/%$/, "").trim()])
                    );
                    const headerRowIndex = sheetRows.findIndex((row) => {
                      const headers = row.map((header) => (header ?? "").replace(/\s+/g, " ").trim().toLowerCase());
                      const hasCodeType = headers.some((header) => header === "code type");
                      const hasPercentage = headers.some(
                        (header) => header === "code type percentage" || header === "code type percentage (%)" || header === "percentage"
                      );
                      return hasCodeType && hasPercentage;
                    });
                    if (headerRowIndex < 0) {
                      setUploadMessage({ isError: true, message: t("The sheet must have Code Type and Code Type Percentage columns") });
                      return;
                    }
                    const headers = sheetRows[headerRowIndex].map((header) => (header ?? "").replace(/\s+/g, " ").trim().toLowerCase());
                    const codeIndex = headers.findIndex((header) => header === "code type");
                    const percentageIndex = headers.findIndex(
                      (header) => header === "code type percentage" || header === "code type percentage (%)" || header === "percentage"
                    );
                    const preview: TableData[] = [];
                    const seenCodeTypes = new Set<string>();
                    sheetRows.slice(headerRowIndex + 1).forEach((row) => {
                      const code = (row[codeIndex] ?? "").trim();
                      const rawPercentage = (row[percentageIndex] ?? "").trim().replace(/%$/, "");
                      if (code === "" && rawPercentage === "") {
                        return;
                      }
                      const reasons: string[] = [];
                      const codeKey = code.toLowerCase();
                      const storedPercentage = code === "" ? undefined : existingCodeTypes.get(codeKey);
                      const sheetDuplicate = code !== "" && seenCodeTypes.has(codeKey);
                      const percentageValid = /^\d+(\.\d{1,2})?$/.test(rawPercentage) && Number(rawPercentage) >= 0 && Number(rawPercentage) <= 100;
                      const sameStoredPercentage = storedPercentage != null && percentageValid && Number(rawPercentage) === Number(storedPercentage);
                      if (code === "") {
                        reasons.push("Code Type is required");
                      } else if (sheetDuplicate) {
                        reasons.push("Code Type is duplicated in the sheet");
                      }
                      if (storedPercentage != null) {
                        reasons.push(
                          sameStoredPercentage ? "Code Type already exists with the same percentage" : "Code Type already exists with the different percentage"
                        );
                      }
                      if (rawPercentage === "" || rawPercentage === ".") {
                        reasons.push("Code Type Percentage is required");
                      } else if (!percentageValid) {
                        reasons.push("Code Type Percentage must be a number from 0 to 100");
                      }
                      if (code !== "" && percentageValid && !sheetDuplicate) {
                        seenCodeTypes.add(codeKey);
                      }
                      const displayPercentage = percentageValid ? `${rawPercentage}%` : rawPercentage;
                      preview.push({
                        id: preview.length + 1,
                        codeType: code,
                        percentage: displayPercentage,
                        reason: reasons.join(". "),
                        canOverride: storedPercentage != null && percentageValid && !sameStoredPercentage && !sheetDuplicate ? 1 : 0,
                      });
                      if (reasons.length > 0) {
                        return;
                      }
                    });
                    if (preview.length === 0) {
                      setUploadMessage({ isError: true, message: t("The sheet has no records") });
                      return;
                    }
                    if (preview.some((row) => row.reason !== "")) {
                      setUploadOpen(false);
                      setUploadFile(null);
                      setUploadMessage({ isError: false, message: "" });
                      setUpdateExisting(false);
                      setPreviewRows(preview);
                      setPreviewOpen(true);
                      return;
                    }
                    saveCodeTypes(preview)
                      .then(() => {
                        setUploadOpen(false);
                        setUploadFile(null);
                        setUploadMessage({ isError: false, message: "" });
                      })
                      .catch((error) => setUploadMessage({ isError: true, message: apiError(error) }));
                  })
                  .catch(() => {
                    setUploadMessage({ isError: true, message: t("Unable to read the file. Upload an Excel sheet") });
                  });
              }}
            >
              {t("Upload")}
            </Button>
          </Group>
        }
        withCloseButton={true}
        onClose={() => {
          setUploadOpen(false);
          setUploadFile(null);
          setUploadMessage({ isError: false, message: "" });
        }}
        size="35%"
      >
        <Grid pb={15} pl={15} pr={15} columns={12} m={0}>
          <Grid.Col span={12}>
            <Button variant="light" color="blue" onClick={downloadCodeTypeTemplate}>
              {t("Download Template")}
            </Button>
          </Grid.Col>
          <Grid.Col span={12}>
            <FileInput
              label={t("Code Type Sheet")}
              description={t("Excel columns: Code Type, Code Type Percentage")}
              value={uploadFile}
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={(file) => {
                setUploadFile(file);
                setUploadMessage({ isError: false, message: "" });
              }}
              clearable
              icon={<Icon icon={faUpload} />}
            />
          </Grid.Col>
          {uploadMessage.message !== "" ? (
            <Grid.Col span={12}>
              <Text size={theme.fontSizes?.sm} color={uploadMessage.isError ? "red" : "green"}>
                {uploadMessage.message}
              </Text>
            </Grid.Col>
          ) : null}
        </Grid>
      </Modal>
      <Modal
        opened={previewOpen}
        header={t("Upload Preview")}
        footer={
          <Group className={classes.modalFooter}>
            <Button variant="default" onClick={closePreview}>
              {t("Close")}
            </Button>
            {uploadRows.length > 0 ? (
              <Button
                onClick={() => {
                  saveCodeTypes(uploadRows)
                    .then(() => {
                      closePreview();
                    })
                    .catch((error) => setUploadMessage({ isError: true, message: apiError(error) }));
                }}
              >
                {`${t("Upload Valid Records")} (${uploadRows.length})`}
              </Button>
            ) : null}
          </Group>
        }
        withCloseButton={true}
        onClose={closePreview}
        size="70%"
      >
        <div style={{ padding: "10px 15px 15px" }}>
          <TableView
            data={previewForUpload}
            columns={[
              { key: "codeType", label: "Code Type", width: "40%" },
              { key: "percentage", label: "Code Type Percentage (%)", width: "18%", dataAlign: "right" },
              { key: "reason", label: "Failed Reason", width: "42%", color: "red" },
            ]}
            fixedLayout
            height={360}
          />
          {errorRecordCount > 0 ? (
            <Alert
              color="yellow"
              variant="light"
              mt={8}
              styles={{
                root: { padding: "4px 10px" },
                message: { fontSize: theme.fontSizes?.sm },
              }}
            >
              <b>{t("NOTE")}: </b>
              {`${t("Edit the file to make all records valid, then upload it again.")} (${failedReasonSummary(previewForUpload)})`}
            </Alert>
          ) : null}
          {canUpdateExisting ? (
            <div style={{ marginTop: 8 }}>
              {updateExisting ? (
                <Checkbox checked label={t("Update existing code types with the new uploaded percentage")} onChange={() => setUpdateExisting(false)} />
              ) : (
                <PopConfirm
                  confirmText={t("Update existing Code Types?")}
                  width={280}
                  position="top-start"
                  buttonPosition="right"
                  onConfirm={() => setUpdateExisting(true)}
                >
                  <Checkbox
                    checked={false}
                    label={t("Update existing code types with the new uploaded percentage")}
                    onChange={() => undefined}
                    styles={{ input: { pointerEvents: "none" }, label: { cursor: "pointer" } }}
                  />
                </PopConfirm>
              )}
            </div>
          ) : null}
        </div>
      </Modal>
    </div>
  );
};

export default InsuranceMaster;
